import { useEffect, useState, useRef } from "react";

const TYPING_SPEED = 60;
const DELETING_SPEED = 30;
const PAUSE_AFTER_TYPE = 1800;
const PAUSE_AFTER_DELETE = 300;

type Phase = "typing" | "pausing" | "deleting" | "waiting";

export function useTypingPlaceholder(phrases: string[]): string {
  const [text, setText] = useState("");
  const phaseRef = useRef<Phase>("typing");
  const indexRef = useRef(0);
  const charRef = useRef(0);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    function tick() {
      const phase = phaseRef.current;
      const phrase = phrases[indexRef.current];

      switch (phase) {
        case "typing": {
          const next = charRef.current + 1;
          setText(phrase.slice(0, next));
          charRef.current = next;
          if (next >= phrase.length) {
            phaseRef.current = "pausing";
            timeout = setTimeout(tick, PAUSE_AFTER_TYPE);
          } else {
            timeout = setTimeout(tick, TYPING_SPEED);
          }
          break;
        }
        case "pausing": {
          phaseRef.current = "deleting";
          timeout = setTimeout(tick, 0);
          break;
        }
        case "deleting": {
          const next = charRef.current - 1;
          setText(phrase.slice(0, next));
          charRef.current = next;
          if (next <= 0) {
            phaseRef.current = "waiting";
            timeout = setTimeout(tick, PAUSE_AFTER_DELETE);
          } else {
            timeout = setTimeout(tick, DELETING_SPEED);
          }
          break;
        }
        case "waiting": {
          indexRef.current = (indexRef.current + 1) % phrases.length;
          charRef.current = 0;
          phaseRef.current = "typing";
          timeout = setTimeout(tick, 0);
          break;
        }
      }
    }

    timeout = setTimeout(tick, 500);
    return () => clearTimeout(timeout);
  }, [phrases]);

  return text;
}
